import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Document, Page } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import { Button, Input, notification, Select, Spin } from 'antd';
import { HighlightRect, PdfDocument, PdfHighlight } from '../../../sdk/@types/Acertei';
import PdfDocumentService from '../../../sdk/services/PdfDocumentService';
import PdfHighlightService from '../../../sdk/services/PdfHighlightService';
import QuestaoService from '../../../sdk/services/QuestaoService';
import AuthService from '../../auth/Authorization.service';
import useHighlightDraw from './useHighlightDraw';

const { Option } = Select;

const BASE_URL = process.env.REACT_APP_AUTH_SERVER_BASE_URL ?? '';

const getDocId = (doc: PdfDocument): string => doc.ID || '';

const getDocByAnyId = (docs: PdfDocument[], id: string): PdfDocument | undefined =>
  docs.find(d => getDocId(d) === id);

interface PdfAnnotatorProps {
  pdfDocId?: string;
  fileKey?: string;
  questionId?: string;
  questionLabel?: string;
  onSave?: () => void;
}

export default function PdfAnnotator({ pdfDocId: pdfDocIdProp, fileKey: fileKeyProp, questionId, questionLabel, onSave }: PdfAnnotatorProps) {
  const [numPages, setNumPages] = useState(0);
  const [selectedPage, setSelectedPage] = useState(1);
  const [currentRect, setCurrentRect] = useState<HighlightRect | null>(null);
  const [existingHighlight, setExistingHighlight] = useState<PdfHighlight | null>(null);
  const [selectedQuestion, setSelectedQuestion] = useState<string | null>(questionId ?? null);
  const [selectedPdfDoc, setSelectedPdfDoc] = useState<string | null>(pdfDocIdProp ?? null);
  const [pdfDocs, setPdfDocs] = useState<PdfDocument[]>([]);
  const [allHighlights, setAllHighlights] = useState<PdfHighlight[]>([]);
  const [currentFileKey, setCurrentFileKey] = useState<string | null>(fileKeyProp || null);
  const [saving, setSaving] = useState(false);
  const [internalPickerIndex, setInternalPickerIndex] = useState<number | null>(null);
  const [questionOptions, setQuestionOptions] = useState<{ id: string; label: string }[]>([]);
  const [questionSearching, setQuestionSearching] = useState(false);
  const [visiblePages, setVisiblePages] = useState<Set<number>>(new Set([0, 1, 2]));
  const [pageWidth, setPageWidth] = useState(600);

  const reviewMode = new URLSearchParams(window.location.search).get('reviewMode') === 'true';

  const pageRefs = useRef<(HTMLDivElement | null)[]>([]);
  const pdfContainerRef = useRef<HTMLDivElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const searchTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    PdfDocumentService.list().then(data => setPdfDocs(data ?? [])).catch(() => setPdfDocs([]));
  }, []);

  useEffect(() => {
    if (pdfDocIdProp) setSelectedPdfDoc(pdfDocIdProp);
  }, [pdfDocIdProp]);

  useEffect(() => {
    if (!questionId) return;
    PdfHighlightService.getByQuestion(questionId)
      .then(h => {
        if (h) {
          setExistingHighlight(h);
          setCurrentRect(h.rect);
          setSelectedPage(h.page);
          setSelectedPdfDoc(h.pdf_doc_id);
        }
      })
      .catch(() => {});
  }, [questionId]);

  useEffect(() => {
    if (fileKeyProp) setCurrentFileKey(fileKeyProp);
  }, [fileKeyProp]);

  useEffect(() => {
    if (!selectedPdfDoc) return;
    if (!fileKeyProp) {
      const doc = getDocByAnyId(pdfDocs, selectedPdfDoc);
      if (doc) {
        setCurrentFileKey(doc.FileKey || '');
        const idx = pdfDocs.indexOf(doc);
        if (idx !== -1) setInternalPickerIndex(idx);
      }
    }
    PdfHighlightService.getByPdf(selectedPdfDoc)
      .then(data => setAllHighlights(data ?? []))
      .catch(() => setAllHighlights([]));
  }, [selectedPdfDoc, pdfDocs, fileKeyProp]);

  useEffect(() => {
    if (!selectedQuestion || !allHighlights.length) return;
    const existing = allHighlights.find(h => h.question_id === selectedQuestion);
    if (existing) {
      setExistingHighlight(existing);
      setCurrentRect(existing.rect);
      setSelectedPage(existing.page);
    } else if (!questionId) {
      setExistingHighlight(null);
      setCurrentRect(null);
    }
  }, [selectedQuestion, allHighlights, questionId]);

  useEffect(() => {
    const update = () => {
      if (pdfContainerRef.current) {
        setPageWidth(Math.max(300, pdfContainerRef.current.offsetWidth - 32));
      }
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  useEffect(() => {
    if (!numPages) return;
    const observer = new IntersectionObserver(
      entries => {
        setVisiblePages(prev => {
          const next = new Set(prev);
          entries.forEach(entry => {
            const idx = parseInt(entry.target.getAttribute('data-page-index') || '0');
            if (entry.isIntersecting) {
              for (let i = Math.max(0, idx - 2); i <= Math.min(numPages - 1, idx + 2); i++) {
                next.add(i);
              }
            }
          });
          return next;
        });
      },
      { rootMargin: '300px' }
    );
    pageRefs.current.forEach(el => { if (el) observer.observe(el); });
    return () => observer.disconnect();
  }, [numPages]);

  const scrollToPage = useCallback((page: number) => {
    pageRefs.current[page - 1]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setSelectedPage(page);
  }, []);

  const scrollToHighlight = useCallback((h: PdfHighlight) => {
    setExistingHighlight(h);
    setCurrentRect(h.rect);
    setSelectedQuestion(h.question_id ?? null);
    scrollToPage(h.page);
  }, [scrollToPage]);

  const handleRectChange = useCallback((rect: HighlightRect, page: number) => {
    setCurrentRect(rect);
    setSelectedPage(page);
  }, []);

  const [overlayCursor, setOverlayCursor] = useState<string>('crosshair');

  const updateCursor = useCallback((e: React.MouseEvent, pageIndex: number) => {
    if (!currentRect || selectedPage !== pageIndex + 1) {
      setOverlayCursor('crosshair');
      return;
    }
    const el = pageRefs.current[pageIndex];
    if (!el) return;
    const elRect = el.getBoundingClientRect();
    const mx = e.clientX - elRect.left;
    const my = e.clientY - elRect.top;
    const ex = (currentRect.x / 100) * el.offsetWidth;
    const ey = (currentRect.y / 100) * el.offsetHeight;
    const ew = (currentRect.w / 100) * el.offsetWidth;
    const eh = (currentRect.h / 100) * el.offsetHeight;
    if (Math.abs(mx - (ex + ew)) <= 10 && Math.abs(my - (ey + eh)) <= 10) {
      setOverlayCursor('se-resize');
    } else if (mx >= ex && mx <= ex + ew && my >= ey && my <= ey + eh) {
      setOverlayCursor('move');
    } else {
      setOverlayCursor('crosshair');
    }
  }, [currentRect, selectedPage]);

  const { handleMouseDown, handleMouseMove, handleMouseUp } = useHighlightDraw({
    existingRect: currentRect,
    selectedPage,
    pageRefs,
    onChange: handleRectChange,
  });

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (!currentRect) return;
    const step = e.shiftKey ? 0.2 : 1;
    let r = { ...currentRect };

    if (e.key === 'ArrowLeft') { e.preventDefault(); r = { ...r, x: r.x - step }; }
    else if (e.key === 'ArrowRight') { e.preventDefault(); r = { ...r, x: r.x + step }; }
    else if (e.key === 'ArrowUp') { e.preventDefault(); r = { ...r, y: r.y - step }; }
    else if (e.key === 'ArrowDown') { e.preventDefault(); r = { ...r, y: r.y + step }; }
    else if (e.key === 'Enter') { e.preventDefault(); handleSave(); return; }
    else if (e.key === 'Delete' || e.key === 'Backspace') { e.preventDefault(); handleDelete(); return; }
    else return;

    setCurrentRect(r);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentRect]);

  const handleQuestionSearch = useCallback((value: string) => {
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    if (!value.trim()) { setQuestionOptions([]); return; }
    setQuestionSearching(true);
    searchTimeout.current = setTimeout(async () => {
      try {
        const questions = await QuestaoService.getQuestoesPorFiltro({ statement: value });
        setQuestionOptions(
          questions.slice(0, 30).map(q => ({
            id: String(q.id),
            label: `[${q.testingAgency?.name ?? ''}] ${(q.statement ?? '').slice(0, 80)}`,
          }))
        );
      } finally {
        setQuestionSearching(false);
      }
    }, 400);
  }, []);

  const handleSave = async () => {
    if (!currentRect || !selectedQuestion || !selectedPdfDoc) return;
    setSaving(true);
    try {
      if (existingHighlight) {
        await PdfHighlightService.update(existingHighlight.highlight_id, {
          page: selectedPage,
          rect: currentRect,
          reviewed: reviewMode ? true : existingHighlight.reviewed,
        });
        notification.success({ message: 'Highlight atualizado.' });
      } else {
        await PdfHighlightService.create({
          pdf_doc_id: selectedPdfDoc,
          question_id: selectedQuestion,
          page: selectedPage,
          rect: currentRect,
        });
        notification.success({ message: 'Highlight criado.' });
      }
      const updated = await PdfHighlightService.getByPdf(selectedPdfDoc);
      setAllHighlights(updated ?? []);
      onSave?.();
    } catch {
      notification.error({ message: 'Erro ao salvar highlight.' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!existingHighlight) return;
    setSaving(true);
    try {
      await PdfHighlightService.delete(existingHighlight.highlight_id);
      setExistingHighlight(null);
      setCurrentRect(null);
      notification.success({ message: 'Highlight removido.' });
      if (selectedPdfDoc) {
        const updated = await PdfHighlightService.getByPdf(selectedPdfDoc);
        setAllHighlights(updated ?? []);
      }
      onSave?.();
    } catch {
      notification.error({ message: 'Erro ao remover highlight.' });
    } finally {
      setSaving(false);
    }
  };

  const fileSource = useMemo(
    () =>
      currentFileKey
        ? {
            url: `${BASE_URL}/files/${currentFileKey}`,
            httpHeaders: { Authorization: `Bearer ${AuthService.getAccessToken()}` },
          }
        : null,
    [currentFileKey]
  );

  const pendingReview = allHighlights.filter(h => !h.reviewed).length;

  return (
    <div
      ref={containerRef}
      className="flex h-full bg-gray-100 dark:bg-[#0a0f0d] text-gray-900 dark:text-white"
      tabIndex={0}
      onKeyDown={handleKeyDown}
      style={{ outline: 'none' }}
    >
      {/* Sidebar */}
      <div className="w-72 flex-shrink-0 flex flex-col gap-3 p-4 bg-white dark:bg-[#0f1a10] border-r border-gray-200 dark:border-[#1a2e1c] overflow-y-auto">
        {/* Question selector */}
        {!questionId ? (
          <div>
            <div className="text-xs text-gray-500 dark:text-[#6b8f6e] mb-1">Questão</div>
            <Select
              showSearch
              placeholder="Buscar questão..."
              filterOption={false}
              onSearch={handleQuestionSearch}
              onChange={val => setSelectedQuestion(val as string)}
              loading={questionSearching}
              className="w-full"
              size="small"
              notFoundContent={questionSearching ? <Spin size="small" /> : 'Digite para buscar'}
            >
              {questionOptions.map(opt => (
                <Option key={opt.id} value={opt.id}>{opt.label}</Option>
              ))}
            </Select>
          </div>
        ) : (
          <div>
            <div className="text-xs text-gray-500 dark:text-[#6b8f6e] mb-1">Questão</div>
            <div className="text-sm text-[#a3e635] bg-gray-100 dark:bg-[#0a0f0d] rounded p-2 border border-gray-200 dark:border-[#1a2e1c]">
              {questionLabel ?? questionId}
            </div>
          </div>
        )}

        {/* PDF doc picker (shown only when pdfDocId is not passed as prop) */}
        {!pdfDocIdProp && (
          <div>
            <div className="text-xs text-gray-500 dark:text-[#6b8f6e] mb-1">Documento PDF</div>
            <Select
              placeholder="Selecionar PDF..."
              value={internalPickerIndex ?? undefined}
              onChange={(idx: number) => {
                setInternalPickerIndex(idx);
                const doc = pdfDocs[idx];
                if (doc) {
                  const id = getDocId(doc);
                  setSelectedPdfDoc(id);
                  if (!fileKeyProp) setCurrentFileKey(doc.FileKey || '');
                }
              }}
              className="w-full"
              size="small"
              notFoundContent="Nenhum documento cadastrado"
            >
              {pdfDocs.map((doc, i) => (
                <Option key={i} value={i}>{doc.Title}</Option>
              ))}
            </Select>
          </div>
        )}

        {/* Page jump */}
        {numPages > 0 && (
          <div>
            <div className="text-xs text-gray-500 dark:text-[#6b8f6e] mb-1">Ir para página</div>
            <Input.Search
              size="small"
              placeholder={`1 – ${numPages}`}
              enterButton="Ir"
              onSearch={val => {
                const n = parseInt(val);
                if (n >= 1 && n <= numPages) scrollToPage(n);
              }}
            />
          </div>
        )}

        {/* Highlights list */}
        {allHighlights.length > 0 && (
          <div>
            <div className="text-xs text-[#6b8f6e] mb-1 flex items-center gap-2">
              Highlights
              {reviewMode && pendingReview > 0 && (
                <span className="text-orange-400">{pendingReview} pendentes</span>
              )}
            </div>
            <div className="flex flex-col gap-1 max-h-48 overflow-y-auto">
              {allHighlights.map(h => (
                <button
                  key={h.highlight_id}
                  onClick={() => scrollToHighlight(h)}
                  className={`text-left text-xs px-2 py-1 rounded border transition-colors ${
                    existingHighlight?.highlight_id === h.highlight_id
                      ? 'border-[#a3e635] bg-[#a3e635]/10 text-[#a3e635]'
                      : reviewMode
                        ? h.reviewed
                          ? 'border-[#82ca9d] text-[#82ca9d] hover:bg-[#82ca9d]/10'
                          : 'border-orange-400 text-orange-400 hover:bg-orange-400/10'
                        : 'border-gray-200 dark:border-[#1a2e1c] text-gray-500 dark:text-[#6b8f6e] hover:border-[#a3e635]'
                  }`}
                >
                  Pg. {h.page} — {h.reviewed ? '✓ revisado' : '⏳ pendente'}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="mt-auto flex flex-col gap-2 pt-2 border-t border-gray-200 dark:border-[#1a2e1c]">
          {currentRect && (
            <div className="text-xs text-gray-500 dark:text-[#6b8f6e]">
              Setas: nudge 1% · Shift+Setas: 0,2% · Enter: salvar · Del: remover
            </div>
          )}
          <Button
            size="small"
            loading={saving}
            disabled={!currentRect || !selectedQuestion || !selectedPdfDoc}
            onClick={handleSave}
            style={{ background: '#a3e635', borderColor: '#a3e635', color: '#000', fontWeight: 600 }}
          >
            {reviewMode ? 'Confirmar posição' : 'Salvar highlight'}
          </Button>
          {existingHighlight && (
            <Button danger size="small" loading={saving} onClick={handleDelete}>
              Remover vínculo
            </Button>
          )}
        </div>
      </div>

      {/* PDF area */}
      <div ref={pdfContainerRef} className="flex-1 overflow-y-auto p-4 select-none">
        {!fileSource ? (
          <div className="flex items-center justify-center h-64 text-gray-400 dark:text-[#6b8f6e]">
            Selecione um documento PDF para começar
          </div>
        ) : (
          <Document
            file={fileSource}
            onLoadError={err => console.error('[PdfAnnotator] PDF load error:', err, '| fileSource:', fileSource)}
            onLoadSuccess={({ numPages: n }) => {
              setNumPages(n);
              setVisiblePages(new Set([0, 1, 2]));
              pageRefs.current = new Array(n).fill(null);
            }}
            loading={
              <div className="flex justify-center p-8">
                <Spin tip="Carregando PDF..." />
              </div>
            }
          >
            {Array.from({ length: numPages }, (_, i) => (
              <div
                key={i}
                ref={el => { pageRefs.current[i] = el; }}
                data-page-index={String(i)}
                className="relative mb-2"
                style={{ minHeight: 800 }}
              >
                {visiblePages.has(i) ? (
                  <>
                    <Page
                      pageNumber={i + 1}
                      width={pageWidth}
                      renderTextLayer={false}
                      renderAnnotationLayer={false}
                    />
                    <div
                      className="absolute inset-0"
                      style={{ cursor: overlayCursor }}
                      onMouseDown={e => handleMouseDown(e, i)}
                      onMouseMove={e => { handleMouseMove(e, i); updateCursor(e, i); }}
                      onMouseUp={handleMouseUp}
                      onMouseLeave={() => setOverlayCursor('crosshair')}
                    >
                      {/* Existing saved highlight */}
                      {existingHighlight?.page === i + 1 && (
                        <div
                          className="absolute pointer-events-none"
                          style={{
                            left: `${existingHighlight.rect.x}%`,
                            top: `${existingHighlight.rect.y}%`,
                            width: `${existingHighlight.rect.w}%`,
                            height: `${existingHighlight.rect.h}%`,
                            background: 'rgba(163, 230, 53, 0.22)',
                            border: '2px solid #a3e635',
                            borderRadius: 3,
                          }}
                        />
                      )}
                      {/* Rect being drawn/moved/resized */}
                      {currentRect && selectedPage === i + 1 && (
                        <div
                          className="absolute pointer-events-none"
                          style={{
                            left: `${currentRect.x}%`,
                            top: `${currentRect.y}%`,
                            width: `${currentRect.w}%`,
                            height: `${currentRect.h}%`,
                            background: 'rgba(163, 230, 53, 0.15)',
                            border: '2px dashed #a3e635',
                            borderRadius: 3,
                          }}
                        >
                          {/* resize handle — visual only, events handled by overlay */}
                          <div
                            style={{
                              position: 'absolute',
                              right: -5,
                              bottom: -5,
                              width: 10,
                              height: 10,
                              background: '#a3e635',
                              borderRadius: 2,
                            }}
                          />
                        </div>
                      )}
                      {/* Other highlights — always shown, clickable to copy area */}
                      {allHighlights
                        .filter(h => h.page === i + 1 && h.highlight_id !== existingHighlight?.highlight_id)
                        .map(h => (
                          <div
                            key={h.highlight_id}
                            className="absolute cursor-pointer"
                            title="Clique para copiar esta área"
                            onMouseDown={e => e.stopPropagation()}
                            onClick={e => {
                              e.stopPropagation();
                              setCurrentRect(h.rect);
                              setSelectedPage(h.page);
                            }}
                            style={{
                              left: `${h.rect.x}%`,
                              top: `${h.rect.y}%`,
                              width: `${h.rect.w}%`,
                              height: `${h.rect.h}%`,
                              background: reviewMode
                                ? h.reviewed ? 'rgba(130, 202, 157, 0.15)' : 'rgba(251, 146, 60, 0.15)'
                                : 'rgba(136, 132, 216, 0.15)',
                              border: `2px solid ${reviewMode
                                ? h.reviewed ? '#82ca9d' : '#fb923c'
                                : '#8884d8'}`,
                              borderRadius: 3,
                            }}
                          />
                        ))}
                    </div>
                  </>
                ) : (
                  <div style={{ height: 800, background: '#0f1a10', borderRadius: 4 }} />
                )}
              </div>
            ))}
          </Document>
        )}
      </div>
    </div>
  );
}
